/**
 * What the public order flow (Choose Number -> Select Plan -> Checkout) sells,
 * and how it describes it.
 *
 * Prices are NOT written here. They come from /api/public/plans (the
 * subscription_plans rows), and the server bills the row's own Stripe price,
 * so the figure on the card and the figure on the Stripe charge come from the
 * same row. This file only decides:
 *
 *   - which (plan, interval) pairs this page offers. Lite is ANNUAL ONLY here
 *     (owner decision 2026-10-25). The monthly Lite row stays active: existing
 *     monthly subscribers are untouched, and the register wizard's default
 *     (DEFAULT_SIGNUP_PLAN_ID = 'lite-plan-monthly') still points at it;
 *   - the card copy, matched to the limephone.io pricing table;
 *   - the order-summary wording on the checkout page.
 *
 * ⚠ The yearly ids are from an older lineup and are named one tier off:
 * starter_yearly is LITE ($90/yr), growth_yearly is STARTER ($180/yr),
 * business_yearly is GROWTH ($450/yr). /api/public/plans groups rows by NAME,
 * which is right, so nothing here ever reads meaning from an id.
 */
import type { Plan, PlanBase } from "./orderFlow";

export type BillingInterval = "month" | "year";

/** Plans this page sells on the yearly price only. */
export const ANNUAL_ONLY_PLAN_NAMES: ReadonlySet<string> = new Set(["Lite"]);

export const FAIR_USAGE_URL = "https://limephone.io/legal/fair-usage-policy";
export const PRICES_FOOTNOTE = "All prices in USD. Applicable taxes added at checkout.";
export const FAIR_USAGE_FOOTNOTE =
  "*Unlimited calling and messaging within the US, UK and Canada is subject to our fair usage policy.";

export interface PlanCardCopy {
  eyebrow: string;
  tagline: string;
  chip: string;
  usersIncluded: number;
  featuresHeading: string;
  features: string[];
  badge?: string;
}

/** Card copy by plan NAME (the grouping key /api/public/plans uses). */
export const PLAN_CARD_COPY: Record<string, PlanCardCopy> = {
  Lite: {
    eyebrow: "FOR SOLO PROFESSIONALS",
    tagline: "A dedicated work number with the essentials to call, text, and stay available.",
    chip: "1 user and 1 free number included",
    usersIncluded: 1,
    featuresHeading: "All plans include:",
    features: [
      "One business number (US, UK or Canada)",
      "Unlimited calls and texts to the US, UK and Canada*",
      "Unlimited inbound calls and SMS",
      "Forward calls to any phone or SIP device",
      "Web, browser and Android apps",
      "Voicemail transcription and built-in CRM",
      "Buy international numbers",
    ],
  },
  Starter: {
    eyebrow: "FOR SMALL TEAMS",
    tagline: "Share numbers, calls, texts, and customer context without paying for every basic tool separately.",
    chip: "3 users and 1 free number included",
    usersIncluded: 3,
    featuresHeading: "Everything in Lite, plus:",
    badge: "POPULAR",
    features: [
      "Shared SMS and call inbox for the team",
      "AI call summaries",
      "Auto attendant and call distribution",
      "Bulk SMS",
      "Team analytics and reporting",
      "Lower international call and SMS rates",
      "Priority email support",
    ],
  },
  Growth: {
    eyebrow: "FOR GROWING BUSINESSES",
    tagline: "Advanced controls, integrations, and hands-on support for customer-facing teams.",
    chip: "5 users and 1 free number included",
    usersIncluded: 5,
    featuresHeading: "Everything in Starter, plus:",
    features: [
      "AI Voice Agent included (100 minutes/month)",
      "Business caller ID",
      "API, webhooks and single sign-on",
      "Advanced automation and custom integrations",
      "Concierge KYC and dedicated onboarding",
      "Priority email and chat support",
    ],
  },
};

export function planCopy(name: string): PlanCardCopy | undefined {
  return PLAN_CARD_COPY[name];
}

export function isAnnualOnly(base: Pick<PlanBase, "name">): boolean {
  return ANNUAL_ONLY_PLAN_NAMES.has(base.name);
}

function hasInterval(base: PlanBase, interval: BillingInterval): boolean {
  return interval === "year"
    ? !!base.yearlyPlanId && base.yearlyPrice > 0
    : !!base.monthlyPlanId && base.monthlyPrice > 0;
}

/** The intervals this page will sell `base` on. */
export function offeredIntervals(base: PlanBase): BillingInterval[] {
  if (isAnnualOnly(base)) return hasInterval(base, "year") ? ["year"] : [];
  return (["month", "year"] as const).filter((i) => hasInterval(base, i));
}

/** Whether the page shows a Monthly/Annual choice for this plan. */
export function offersBothIntervals(base: PlanBase): boolean {
  return offeredIntervals(base).length === 2;
}

/** `wanted` when this plan is sold on it, otherwise the interval it is sold on. */
export function intervalForPlan(base: PlanBase, wanted: BillingInterval): BillingInterval | undefined {
  const offered = offeredIntervals(base);
  return offered.includes(wanted) ? wanted : offered[0];
}

/** The purchasable Plan for `base` at `interval`, or undefined. */
export function planAt(base: PlanBase, interval: BillingInterval): Plan | undefined {
  if (!offeredIntervals(base).includes(interval)) return undefined;
  return {
    id: interval === "year" ? base.yearlyPlanId : base.monthlyPlanId,
    name: base.name,
    price: interval === "year" ? base.yearlyPrice : base.monthlyPrice,
    interval,
    features: base.features,
    popular: base.popular,
  };
}

/** One card per plan, each on the toggle's interval where it is sold on it. */
export function offeredPlans(basePlans: PlanBase[], wanted: BillingInterval): Plan[] {
  const out: Plan[] = [];
  for (const base of basePlans) {
    const interval = intervalForPlan(base, wanted);
    const plan = interval ? planAt(base, interval) : undefined;
    if (plan) out.push(plan);
  }
  return out;
}

export function baseForPlanId(basePlans: PlanBase[], planId: string | null | undefined): PlanBase | undefined {
  if (!planId) return undefined;
  return basePlans.find((b) => b.monthlyPlanId === planId || b.yearlyPlanId === planId);
}

/**
 * The plan a stored or linked id names, ONLY when this page sells it. A Lite
 * MONTHLY id (an old /start link, a win-back email, an earlier visit) returns
 * undefined: the page must not preselect a plan it no longer offers, and must
 * never silently swap it for a $90 annual charge either. The visitor picks.
 */
export function resolveOfferedPlan(basePlans: PlanBase[], planId: string | null | undefined): Plan | undefined {
  const base = baseForPlanId(basePlans, planId);
  if (!base) return undefined;
  const interval: BillingInterval = base.yearlyPlanId === planId ? "year" : "month";
  return planAt(base, interval);
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** What a year of monthly billing costs over the yearly price. 0 if none. */
export function savingsPerYear(base: PlanBase): number {
  if (!hasInterval(base, "month") || !hasInterval(base, "year")) return 0;
  return Math.max(0, round2(base.monthlyPrice * 12 - base.yearlyPrice));
}

/**
 * The best annual discount among plans sold on BOTH intervals, rounded down so
 * the toggle never overstates it. Lite is annual-only here, so it has no
 * monthly price to save against on this page and is left out.
 */
export function maxAnnualSavingsPercent(basePlans: PlanBase[]): number {
  let best = 0;
  for (const base of basePlans) {
    if (!offersBothIntervals(base)) continue;
    const pct = (savingsPerYear(base) / (base.monthlyPrice * 12)) * 100;
    if (pct > best) best = pct;
  }
  return Math.floor(best);
}

/** $90, $37.50 — whole dollars without cents. */
export function formatUsd(n: number): string {
  const v = round2(n);
  return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
}

/** $180.00 — always with cents, for totals. */
export function formatUsdCents(n: number): string {
  return `$${round2(n).toFixed(2)}`;
}

export interface PriceDisplay {
  amount: string;
  unit: string;
  notes: string[];
}

/** The price block on a plan card. */
export function priceDisplay(base: PlanBase, interval: BillingInterval): PriceDisplay {
  if (isAnnualOnly(base)) {
    return {
      amount: formatUsd(base.yearlyPrice),
      unit: "/year",
      notes: [`Annual plan only · works out at ${formatUsd(base.yearlyPrice / 12)} a month`],
    };
  }
  if (interval === "year") {
    const saving = savingsPerYear(base);
    return {
      amount: formatUsd(base.yearlyPrice / 12),
      unit: "/month total",
      notes: [`Billed ${formatUsd(base.yearlyPrice)} yearly${saving > 0 ? ` · Save ${formatUsd(saving)}/year` : ""}`],
    };
  }
  return { amount: formatUsd(base.monthlyPrice), unit: "/month total", notes: ["Billed monthly"] };
}

export interface OrderSummaryView {
  planLabel: string;
  planPrice: string;
  billingLabel: string;
  usersIncluded: number | null;
  /** "$180.00 today, renews yearly" */
  dueTodayText: string;
  dueToday: number;
  /** Shown under the cycle switch. */
  cycleHint: string | null;
}

export function orderSummary(base: PlanBase, plan: Plan): OrderSummaryView {
  const yearly = plan.interval === "year";
  const saving = savingsPerYear(base);
  let cycleHint: string | null = null;
  if (offersBothIntervals(base) && saving > 0) {
    cycleHint = yearly
      ? `You save ${formatUsd(saving)}/yr with annual billing`
      : `Save ${formatUsd(saving)}/yr by choosing annual`;
  }
  return {
    planLabel: `${plan.name} plan`,
    planPrice: `${formatUsdCents(plan.price)}/${yearly ? "year" : "month"}`,
    billingLabel: yearly ? (isAnnualOnly(base) ? "Billed yearly · annual plan only" : "Billed yearly") : "Billed monthly",
    usersIncluded: planCopy(plan.name)?.usersIncluded ?? null,
    dueTodayText: `${formatUsdCents(plan.price)} today, renews ${yearly ? "yearly" : "monthly"}`,
    dueToday: plan.price,
    cycleHint,
  };
}

/** Shown when the email check refuses an address without saying why. */
export const EMAIL_INVALID_FALLBACK = "Use a permanent business or personal email address.";

/** Why the checkout button is disabled, in plain words. null when it is enabled. */
export function checkoutBlockedReason(s: {
  email: string;
  emailChecking: boolean;
  emailInvalid: boolean;
  /** The refusal text the email field shows; the reason line repeats it. */
  emailInvalidMessage?: string | null;
  /** Not shaped like an email at all (checked on blur, before the server). */
  emailMalformed?: boolean;
  phoneVerified: boolean;
  codeSent: boolean;
  submitting: boolean;
  /** The chosen plan has resolved against the live plan list. */
  planReady?: boolean;
}): string | null {
  if (s.submitting) return null;
  if (s.planReady === false) return "Loading your plan…";
  if (!s.email.trim()) return "Enter your email address to continue.";
  if (s.emailMalformed) return "Enter a valid email address.";
  if (s.emailChecking) return "Checking your email address…";
  if (s.emailInvalid) return s.emailInvalidMessage || EMAIL_INVALID_FALLBACK;
  if (!s.phoneVerified) {
    return s.codeSent
      ? "Enter the 6-digit code we sent to your mobile to continue."
      : "Verify your mobile number to continue.";
  }
  return null;
}

/*
 * Funnel add-ons (AI Receptionist packs, US 10DLC) are NOT sold from the order
 * page right now (owner decision 2026-10-25). The server still accepts
 * `addonIds` (FUNNEL_ADDONS_ENABLED is on in prod), so the client must never
 * send a stale selection: routes that skip Select plan (cancel-return, reload,
 * a tab from the old build) would otherwise add $59-$399/mo on a monthly plan,
 * or be refused on an annual one after the order row exists.
 */
export const FUNNEL_ADDONS_ON_ORDER_PAGE = false;
export const ADDONS_STORAGE_KEY = "checkoutAddonIds";

type AddonStorage = Pick<Storage, "getItem" | "removeItem">;

/**
 * The add-on ids to send with the checkout call. While add-ons are off this is
 * always [] and the stored key is cleared.
 */
export function addonIdsForCheckout(storage: AddonStorage, enabled: boolean = FUNNEL_ADDONS_ON_ORDER_PAGE): string[] {
  try {
    if (!enabled) {
      storage.removeItem(ADDONS_STORAGE_KEY);
      return [];
    }
    const parsed = JSON.parse(storage.getItem(ADDONS_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((a): a is string => typeof a === "string") : [];
  } catch {
    return [];
  }
}

/** The checkout call's JSON body. `addonIds` appears only when there are some. */
export function checkoutSessionBody(googleIdentityToken: string | null, addonIds: string[]): Record<string, unknown> {
  return {
    ...(googleIdentityToken ? { googleIdentityToken } : {}),
    ...(addonIds.length > 0 ? { addonIds } : {}),
  };
}
